// S-3 — HER CIRCLE: THE TWO READS AND THE TWO ACTS, AS A LAW A GATE CAN RUN.
//
// `doFetch` is a PARAMETER (R46), so every answer these routes can give is DRIVEN in the fast tier
// rather than reachable only through a live Chrome on a live Postgres. This module imports NOTHING
// but types, which is the entry requirement that lets a `.mjs` gate load it.
//
// **THE ANSWER SHAPE IS ARGUED PER ROUTE RATHER THAN MADE UNIFORM** (R58/R60/R70's ruling). Both
// reads sit behind sign-in and behind the room's tier, so a refusal is a real thing they can say and
// `people: null` is W5-2/R39's third state: NOT LOADED YET is not the same fact as SHE FOLLOWS
// NOBODY, and drawing the second over the first is how a list that failed to read becomes a
// statement about her (R59's saved shelf, one panel over).
//
// **THE COUNTS RIDE ON EVERY ANSWER, INCLUDING THE ACTS' ANSWERS.** Her order says the counts
// live-update, and the only honest way to do that is to paint what the SERVER says after the write
// rather than to decrement a number on the strength of a tap (R39/R47: read the payload). A removal
// the server refused must not take one off the count on screen.
//
// A 402 is deliberately not a branch here: T8-3/R46's interceptor turns every one of them on this
// site into her purple box, and a second answer for it would be a second door.

export type CirclePerson = {
  ref: string;
  name: string;
  crowned?: boolean;
  owner?: boolean;
  following?: boolean;
  photoUrl?: string | null;
};

export type CircleCounts = { followers: number; following: number };

export type CircleAnswer = {
  // `null` is "this did not land". `[]` is "there is genuinely nobody", and they are two facts.
  people: CirclePerson[] | null;
  total: number | null;
  counts: CircleCounts | null;
  // LR17-2 — **WHO IS LOOKING, WHICH IS WHAT THE FOLLOW CONTROL NEEDS AND CANNOT INFER.**
  // `followState` answers `self` for her own row and `null` when it does not know, so a list with no
  // `me` draws NO tag at all rather than drawing a wrong one — which is R18/R44's direction on a
  // control: refuse rather than resolve. `""` is that unknown, kept out of the useful range by
  // `followState`'s own first clause.
  me: string;
  note: string;
};

export type ActAnswer = { done: boolean; counts: CircleCounts | null; note: string };

export type Doer = (url: string, init?: unknown) => Promise<{ ok: boolean; status: number; json: () => Promise<unknown> }>;

// The one sentence this surface owns when the server has none of its own. Every route behind it
// composes a real refusal, and this is the floor for the case where nothing arrived at all — never a
// claim about her circle, which is the mistake R64/R70 both cost this house.
export const CIRCLE_READ_FAILED = "That could not be read right now.";
export const CIRCLE_ACT_FAILED = "That did not go through.";

// R53's ruling: a 200 carrying no list is a REFUSAL, and the guard is PRESENCE rather than
// truthiness — an empty array is a perfectly good answer and `!d.followers` is true of one.
function readList(data: Record<string, unknown> | null, key: string): CircleAnswer {
  const rows = data ? (data as Record<string, unknown>)[key] : undefined;
  const counts = (data?.counts as CircleCounts | undefined) || null;
  // A ref is only a ref if the SERVER sent a string. Anything else is not knowing who is looking, and
  // `""` is what says so — never a coerced `"undefined"`, which reads as a real ref and would make
  // every row on the list somebody who is not her (R136/R137's coercion lesson, on an identity).
  const me = typeof data?.me === "string" ? data.me : "";
  if (!data || data.ok === false || !Array.isArray(rows)) {
    return { people: null, total: null, counts, me, note: String(data?.message || "") || CIRCLE_READ_FAILED };
  }
  const total = typeof data.total === "number" ? data.total : null;
  return { people: rows as CirclePerson[], total, counts, me, note: "" };
}

async function ask(doFetch: Doer, url: string, init?: unknown): Promise<Record<string, unknown> | null> {
  try {
    const res = await doFetch(url, init);
    // A thrown request is a WAIT or a nothing; a STATUS is an answer (R18). Both end at the same
    // sentence here because this surface has one, but they are read apart so a later round can tell
    // them apart without unpicking the transport.
    const body = await res.json().catch(() => null);
    if (body && typeof body === "object") return body as Record<string, unknown>;
    return null;
  } catch {
    return null;
  }
}

const page = (limit: number, offset: number) => `?limit=${Math.max(1, limit)}&offset=${Math.max(0, offset)}`;

export async function loadFollowers(doFetch: Doer, limit = 30, offset = 0): Promise<CircleAnswer> {
  return readList(await ask(doFetch, `/api/living-room/followers${page(limit, offset)}`), "followers");
}

export async function loadFollowing(doFetch: Doer, limit = 30, offset = 0): Promise<CircleAnswer> {
  return readList(await ask(doFetch, `/api/living-room/following${page(limit, offset)}`), "following");
}

// LR17-3 — **FIND SOMEONE. THE SAME READER OVER ITS OWN KEY, WHICH IS THE WHOLE POINT.**
//
// `readList` is what keeps *a 200 carrying no list is a refusal*, *a non-string `me` is not a ref*
// and *`null` is not `[]`* from having a second spelling on this surface (R28/R29) — and the third
// of those is the one that costs here: a search that failed and a search that found nobody are two
// facts, and painting the second over the first tells her the person she is looking for is not a
// member of this house.
//
// **THE TERM IS NOT NORMALISED HERE AND THAT IS DELIBERATE.** `memberSearchTerm` lives in
// `src/livingRoom.js` beside the store's own use of it; this module's entry requirement is that it
// imports nothing but types (R5/R6/R8 — it is what lets a `.mjs` gate load it), so the CALLER
// normalises and this transport asks exactly what it was handed. What it will not do is send an
// empty query, because a route asked about nothing answers nothing and the round trip is waste.
export async function searchMembers(doFetch: Doer, term: string, limit = 20): Promise<CircleAnswer> {
  const q = String(term ?? "").trim();
  if (!q) return { people: [], total: null, counts: null, me: "", note: "" };
  return readList(
    await ask(doFetch, `/api/living-room/members?q=${encodeURIComponent(q)}&limit=${Math.max(1, limit)}`),
    "members",
  );
}

export async function loadCircleCounts(doFetch: Doer): Promise<CircleCounts | null> {
  const d = await ask(doFetch, "/api/living-room/follow-counts");
  const counts = (d?.counts as CircleCounts | undefined) || null;
  return d && d.ok !== false && counts ? counts : null;
}

const post = (body: unknown) => ({
  method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body),
});

// **REMOVE READS `removed`, NOT `ok`.** The route answers `ok: true` for a request it understood and
// `removed: false` for a row that was not there to take — R39/R47's exact trap, where a client
// reading the wrapper calls a refusal a success and takes a person off her screen who is still
// following her.
export async function removeFollower(doFetch: Doer, ref: string): Promise<ActAnswer> {
  const d = await ask(doFetch, "/api/living-room/followers/remove", post({ ref }));
  const counts = (d?.counts as CircleCounts | undefined) || null;
  if (!d || d.ok === false) return { done: false, counts, note: String(d?.message || "") || CIRCLE_ACT_FAILED };
  return { done: d.removed === true, counts, note: d.removed === true ? "" : CIRCLE_ACT_FAILED };
}

// **UNFOLLOW IS ITEM 7's OWN ROUTE AND IS NEVER BLOCKED** — blocking somebody must not trap her
// follow of them in place. The truth is `following`, which the route answers off the database rather
// than off what was asked for, so the tag can never be painted from the request.
export async function unfollow(doFetch: Doer, ref: string): Promise<ActAnswer> {
  const d = await ask(doFetch, "/api/living-room/follow", post({ ref, following: false }));
  if (!d || d.ok === false) return { done: false, counts: null, note: String(d?.message || "") || CIRCLE_ACT_FAILED };
  return { done: d.following === false, counts: null, note: d.following === false ? "" : CIRCLE_ACT_FAILED };
}

// WHAT THE TWO NUMBERS SAY. A count that has not arrived is drawn as a dash rather than as a zero:
// "she has no followers" and "we could not ask" are different facts and one of them is unkind
// (R18/R64, and it is the same distinction the list above it keeps).
export const countLabel = (n: number | null | undefined): string =>
  (typeof n === "number" && Number.isFinite(n) ? String(n) : "—");

// SHOWING ALL OF THEM? Held as a LAW rather than as `people.length < total` at the call site,
// because a full last page and a list with more behind it are indistinguishable from the page alone.
export const hasMore = (shown: number, total: number | null): boolean =>
  typeof total === "number" && shown < total;
